<template>
  <div class="notifications-page">
    <div class="notifications-container">
      <div class="notifications-head">
        <h2 class="notifications-title">Уведомления</h2>
        <div class="notifications-actions">
          <b-form-radio-group
            v-model="filter"
            buttons
            button-variant="outline-light"
            size="sm"
            :options="filterOptions"
            class="notifications-filter"
          ></b-form-radio-group>
          <b-button
            v-if="unread > 0"
            variant="outline-warning"
            size="sm"
            pill
            @click="markAll"
          >Прочитать все</b-button>
        </div>
      </div>

      <div v-if="loading && !items.length" class="notifications-empty">
        <b-spinner small />
      </div>
      <div v-else-if="!filtered.length" class="notifications-empty">
        {{ filter === 'unread' ? 'Непрочитанных нет' : 'Уведомлений нет' }}
      </div>

      <div v-else class="notifications-list">
        <a
          v-for="n in filtered"
          :key="n.id"
          href="javascript:void(0)"
          class="notifications-item"
          :class="{ 'is-unread': !n.isRead }"
          @click="open(n)"
        >
          <span class="notifications-dot" :style="{ background: dotColor(n.type) }"></span>
          <div class="notifications-body">
            <div class="notifications-row">
              <span class="notifications-item-title">{{ n.title }}</span>
              <span class="notifications-item-time">{{ formatTime(n.createdAt) }}</span>
            </div>
            <div v-if="n.body" class="notifications-item-body">{{ n.body }}</div>
          </div>
        </a>
      </div>

      <div v-if="filtered.length && hasMore" class="notifications-load-more">
        <b-button
          variant="outline-light"
          size="sm"
          pill
          :disabled="loadingMore"
          @click="loadMore"
        >
          <b-spinner v-if="loadingMore" small class="mr-2" />
          Загрузить ещё
        </b-button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapState } from 'vuex'

export default {
  name: 'NotificationsPage',
  data() {
    return {
      filter: 'all',
      filterOptions: [
        { text: 'Все', value: 'all' },
        { text: 'Непрочитанные', value: 'unread' },
      ],
      loading: false,
      loadingMore: false,
      hasMore: true,
    }
  },
  computed: {
    ...mapState({
      items: 'notifications',
      unread: 'notificationsUnread',
    }),
    filtered() {
      if (this.filter === 'unread') return this.items.filter(n => !n.isRead)
      return this.items
    },
  },
  mounted() {
    this.refresh()
  },
  methods: {
    async refresh() {
      this.loading = true
      const list = await this.$store.dispatch('fetchNotifications', { limit: 20 })
      this.hasMore = list.length === 20
      this.loading = false
      this.$store.dispatch('fetchUnreadCount')
    },
    async loadMore() {
      this.loadingMore = true
      const more = await this.$store.dispatch('fetchMoreNotifications')
      this.hasMore = more.length === 20
      this.loadingMore = false
    },
    async open(n) {
      await this.$store.dispatch('markNotificationRead', n.id)
      if (n.link) this.$router.push(n.link).catch(() => {})
    },
    async markAll() {
      await this.$store.dispatch('markAllNotificationsRead')
    },
    dotColor(type) {
      const map = {
        CLASSIFICATION_APPROVED: '#4ade80',
        CLASSIFICATION_REJECTED: '#EF4444',
      }
      return map[type] || '#EFB914'
    },
    formatTime(iso) {
      if (!iso) return ''
      const d = new Date(iso)
      if (Number.isNaN(d.getTime())) return ''
      return d.toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit',
      })
    },
  },
}
</script>

<style scoped>
.notifications-page {
  min-height: 100vh;
  background: #18191D;
  color: #f8fafc;
  padding: 32px 16px;
}
.notifications-container {
  max-width: 720px;
  margin: 0 auto;
}
.notifications-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  gap: 16px;
  flex-wrap: wrap;
}
.notifications-title {
  font-size: 24px;
  font-weight: 700;
  margin: 0;
  color: #f8fafc;
}
.notifications-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}
.notifications-filter ::v-deep .btn { font-size: 12px; }

.notifications-empty {
  text-align: center;
  padding: 60px 0;
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
}

.notifications-list {
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 16px;
  overflow: hidden;
  background: rgba(255, 255, 255, 0.02);
}
.notifications-item {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px 18px;
  text-decoration: none;
  color: #f8fafc;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  transition: background .12s ease;
}
.notifications-item:last-child { border-bottom: 0; }
.notifications-item:hover { background: rgba(255, 255, 255, 0.04); text-decoration: none; color: #f8fafc; }
.notifications-item.is-unread { background: rgba(239, 185, 20, 0.05); }
.notifications-dot {
  flex: 0 0 10px;
  width: 10px;
  height: 10px;
  border-radius: 999px;
  margin-top: 6px;
}
.notifications-body { flex: 1; min-width: 0; }
.notifications-row {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.notifications-item-title { font-size: 14px; font-weight: 600; color: #f8fafc; }
.notifications-item-time { font-size: 11px; color: rgba(255, 255, 255, 0.45); }
.notifications-item-body {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.75);
  margin-top: 4px;
  line-height: 1.5;
}

.notifications-load-more {
  text-align: center;
  margin-top: 18px;
}
</style>
